import { sqliteTable, text, integer } from "drizzle-orm/sqlite-core";

// ============================================================================
// companies — Multi-company ภายใน tenant
// ============================================================================
export const companies = sqliteTable("core_companies", {
  id: text("id").primaryKey(), // ULID
  name: text("name").notNull(),
  abbreviation: text("abbreviation").notNull().unique(), // e.g., "KCR"
  taxId: text("tax_id"),
  address: text("address"),
  phone: text("phone"),
  email: text("email"),
  logoUrl: text("logo_url"),
  defaultCurrency: text("default_currency").notNull().default("THB"),
  fiscalYearStart: text("fiscal_year_start").notNull().default("01-01"), // MM-DD
  businessType: text("business_type").default("general"),
  vatServiceInvoicePolicy: text("vat_service_invoice_policy", {
    enum: ["on_payment", "on_service_completion", "on_due_date"],
  }).default("on_payment"),
  undueOutputVatAccountId: text("undue_output_vat_account_id"),
  undueInputVatAccountId: text("undue_input_vat_account_id"),
  // Reverse-charge VAT payable (ม.83/6, ภ.พ.36) — credited when posting an
  // import_service invoice, cleared when the company remits VAT to RD.
  // Points to the "ภ.พ.36 ค้างจ่าย" account in the chart (default 215103 via seed).
  pp36PayableAccountId: text("pp36_payable_account_id"),
  // Default accounts for accounting operations
  defaultArAccountId: text("default_ar_account_id"), // Accounts Receivable (ลูกหนี้การค้า)
  defaultApAccountId: text("default_ap_account_id"), // Accounts Payable (เจ้าหนี้การค้า)
  defaultCashAccountId: text("default_cash_account_id"), // Cash (เงินสด)
  defaultCustomerAdvanceAccountId: text("default_customer_advance_account_id"), // Customer advance / deposit liability
  defaultSupplierAdvanceAccountId: text("default_supplier_advance_account_id"), // Supplier advance / prepayment asset
  defaultWhtReceivableAccountId: text("default_wht_receivable_account_id"), // WHT Receivable (ภาษีหัก ณ ที่จ่าย - ลูกหนี้)
  defaultWhtPayableAccountId: text("default_wht_payable_account_id"), // WHT Payable (ภาษีหัก ณ ที่จ่าย - เจ้าหนี้)
  // FX gain/loss accounts (#282) — used when payment clears an invoice at a
  // different exchange rate than the one it was booked at. Infrastructure
  // landed in Phase 1; the payment.service.ts posting logic ships in Phase 2
  // once multi-currency amount semantics are audited across the system.
  defaultFxGainAccountId: text("default_fx_gain_account_id"), // e.g., 420103 "กำไรจากอัตราแลกเปลี่ยน"
  defaultFxLossAccountId: text("default_fx_loss_account_id"), // e.g., 540105 "ขาดทุนจากอัตราแลกเปลี่ยน"
  // Unrealized FX revaluation (#292, ม.65 ทวิ) — period-end mark-to-market
  // on outstanding foreign AR/AP. These credit/debit when the revaluation
  // JE posts; the next revaluation reverses prior JE first so balances
  // don't stack. Seed: 420102 / 540104 (unrealized).
  defaultUnrealizedFxGainAccountId: text("default_unrealized_fx_gain_account_id"),
  defaultUnrealizedFxLossAccountId: text("default_unrealized_fx_loss_account_id"),
  // Retained earnings — year-end closing (#280) credits (or debits, on loss)
  // this equity account to persist the period's net profit after zeroing
  // out revenue/expense accounts. Expected: 331000-range "กำไรสะสม" account.
  defaultRetainedEarningsAccountId: text("default_retained_earnings_account_id"),
  // Default price list (#444) — soft FK to `accounting_price_lists.id`.
  // Tenant-wide fallback used by the POS / sales-invoice price resolution
  // chain (see `getItemPrice` in modules/accounting/services/price-list.service.ts):
  //   explicit priceListId > customer.defaultPriceListId
  //     > company.defaultPriceListId > item.defaultRate.
  // Cross-module soft FK (companies → accounting): no DB constraint so the
  // core schema compiles without a hard dependency on the accounting tables
  // (same pattern as defaultArAccountId / defaultCostCenterId above).
  defaultPriceListId: text("default_price_list_id"),
  // Default cost center (#410) — soft FK to `accounting_cost_centers.id`.
  // Populated by `seedAccountingData` on `core.company.created` with the
  // auto-seeded "ส่วนกลาง (Main)" group. Used by fleet / rental / purchase
  // flows to auto-fill cost center in shared modals + the cost-center P&L
  // report. No hard FK constraint: cross-module refs stay soft so each
  // module's schema can compile standalone (same pattern as the accounts
  // above). Nullable so legacy companies created before #410 keep working
  // until their one-shot backfill lands.
  defaultCostCenterId: text("default_cost_center_id"),
  // 1 = เลือกภาษี/หัก ณ ที่จ่าย ต่อรายการ (per line); 0 = รวมทั้งบิล (document level)
  defaultLineLevelTax: integer("default_line_level_tax").notNull().default(0),
  // Epic #443 — Sales→Inventory automation defaults.
  //
  // allowNegativeStock: 0 = block sales-invoice submit when stock would go
  //   below zero (default — safer for retail). 1 = let stock go negative
  //   (drop-ship / pre-order flows). Read by the inventory deduction
  //   listener in `inventory/hooks/index.ts`.
  //
  // defaultCogsAccountId: soft FK to `accounting_accounts.id`. Used by
  //   `accounting/services/public-api/post-invoice-cogs.ts` when the item's
  //   own `expenseAccountId` is null. Falls through to a chart 5* lookup
  //   if both are missing. Seeded by `seedAccountingData` to the auto-
  //   generated 510103 "ต้นทุนขายสินค้า" / Cost of goods sold account.
  //
  // defaultInventoryAccountId: soft FK to `accounting_accounts.id`. The CR
  //   leg of the COGS entry — represents the inventory asset being relieved.
  //   Seeded to 114102 "สินค้าสำเร็จรูป" / Finished goods.
  //
  // Soft FKs (no DB constraint) keep this core schema independent of the
  // accounting tables — same pattern as `defaultArAccountId` / `defaultPriceListId`.
  allowNegativeStock: integer("allow_negative_stock").notNull().default(0),
  defaultCogsAccountId: text("default_cogs_account_id"),
  defaultInventoryAccountId: text("default_inventory_account_id"),
  branchNo: text("branch_no"),
  createdAt: text("created_at").notNull(),
  createdBy: text("created_by"),
  updatedAt: text("updated_at").notNull(),
  updatedBy: text("updated_by"),
  deletedAt: text("deleted_at"),
  deletedBy: text("deleted_by"),
});

// Type exports
export type Company = typeof companies.$inferSelect;
export type NewCompany = typeof companies.$inferInsert;
